import React from 'react';

import { Form } from 'antd';
import { Text, Row, Icon, Modal } from 'farmhub-ui-core';
import styled from 'styled-components';

import { PartnerNote } from 'api';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Empty, dataEmpty, PartnerNoteSkeleton, TitleText } from 'components';
import {
  updatePartnerNote,
  findOnePartnerNote,
  cancelPartnerNoteModal,
} from 'slice';
import { theme } from 'styled';
import { ICONS, getDummyArray, copyText, updateStyleElement } from 'utils';

import BasicInfo from './BasicInfo';
import PartnerNoteEditor from './PartnerNoteEditor';
import PartnerNoteItem from './PartnerNoteItem';
import {
  PartnerNoteWrapper,
  PartnerNoteHeader,
  PartnerNoteContent,
  AddButton,
  ModalStyles,
  Wrapper,
} from './styles';
import UpdateCodeFormModal from './UpdateCodeFormModal';

const PartnerNoteModal = (): JSX.Element => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const modal = useAppSelector(state => state.modal);
  const [isLoading, setIsLoading] = React.useState(true);
  const [notes, setNotes] = React.useState<PartnerNote[]>([]);
  const [prevCode, setPrevCode] = React.useState<string | undefined>('');
  const [isCreateMode, setIsCreateMode] = React.useState(false);
  const [activeFormID, setActiveFormID] = React.useState<string | undefined>(
    undefined
  );
  const [entity, setEntity] = React.useState<any>({});

  const [query, setQuery] = React.useState({
    select: [
      'id',
      'code',
      'note',
      'entity.id',
      'entity.code',
      'entity.domain',
      'entity.website',
      'entity.avatar',
      'entity.name',
      'entity.phoneNumber',
      'entity.domainStatus',
      'entity.email',
      'entity.address',
      'owner.name',
      'ward.id',
      'ward.id',
      'ward.fullName',
      'district.id',
      'district.fullName',
      'province.id',
      'province.fullName',
      'entityOptions.id',
      'option.id',
      'option.content',
      'color.id',
      'color.code',
    ],
    relations: [
      'entity',
      'entity.ward',
      'ward',
      'ward.district',
      'district',
      'ward.province',
      'entity.owner',
      'entity.entityOptions',
      'entityOptions',
      'entityOptions.option',
      'option',
      'option.color',
    ],
  });

  const handleUpdatePrevCode = (val: string | undefined) => {
    setPrevCode(val);
  };

  const handleShowCreateNote = () => {
    setIsCreateMode(true);
    setActiveFormID('CREATE_NOTE');
  };

  const handleCancelCreateNote = () => {
    setIsCreateMode(false);
    setActiveFormID(undefined);
  };

  const handleActiveFormID = (val: string | undefined) => {
    setActiveFormID(val);
  };

  const handleCreateNote = async (val: any) => {
    const newData = {
      id: `${Date.now()}`,
      ...val,
      description: [],
      action: 'add',
    };

    await handleSubmit(newData);
    handleCancelCreateNote();
  };

  const handleDeleteNote = async (val: any) => {
    await handleSubmit({ ...val, action: 'delete' });
  };

  const handleUpdateNote = async (val: any) => {
    await handleSubmit({ ...val, action: 'update' });
  };

  const handleSubmit = async (val: any) => {
    try {
      const res: any = await dispatch(
        updatePartnerNote({
          id: modal?.dataPartnerNoteModal?.id || '',
          body: { note: [val] },
        })
      ).unwrap();

      const { data } = res;
      if (data.note) {
        setNotes(data.note);

        if (data.note.length > 0) {
          updateStyleElement(
            `${modal?.dataPartnerNoteModal?.id}-badge`,
            'visibility',
            'visible'
          );
        } else {
          updateStyleElement(
            `${modal?.dataPartnerNoteModal?.id}-badge`,
            'visibility',
            'hidden'
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    const getPartnerNote = async () => {
      setIsLoading(true);
      try {
        const res: any = await dispatch(
          findOnePartnerNote({
            partnerID: modal?.dataPartnerNoteModal?.id || '',
            option: query,
          })
        ).unwrap();

        const { data } = res;

        if (data?.note) {
          setNotes(data.note ?? []);

          if (data.note.length > 0) {
            updateStyleElement(
              `${modal?.dataPartnerNoteModal?.id}-badge`,
              'visibility',
              'visible'
            );
          } else {
            updateStyleElement(
              `${modal?.dataPartnerNoteModal?.id}-badge`,
              'visibility',
              'hidden'
            );
          }
        }

        if (data?.code) {
          form.setFieldsValue({ code: data?.code });
          setPrevCode(data?.code);
        }

        if (data?.entity) {
          setEntity(data.entity);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (modal.visiblePartnerNoteModal && modal?.dataPartnerNoteModal?.id) {
      getPartnerNote();
    }
  }, [modal.visiblePartnerNoteModal, modal?.dataPartnerNoteModal?.id]);

  const handleClearData = () => {
    setNotes([]);
    form.resetFields();
  };

  const handleCancelModal = () => {
    handleClearData();
    setIsCreateMode(false);

    dispatch(cancelPartnerNoteModal());
  };

  const handleCopyText = () => {
    let result = `Ghi chú khách hàng: ${name}`;

    result += notes
      .map(
        item =>
          `\n - ${item.title}${item.description
            ?.map(ele => `\n     * ${ele.value}`)
            .join('')}`
      )
      .join('');

    copyText(result);
  };

  return (
    <Modal
      centered
      destroyOnClose
      bodyHeight="630px"
      styles={ModalStyles}
      title={
        <TitleText>
          Ghi chú khách hàng
          <Text color="main" size="xs" weight="bold">
            {' '}
            {modal?.dataPartnerNoteModal?.displayName}
          </Text>
        </TitleText>
      }
      visible={modal.visiblePartnerNoteModal}
      width="520px"
      onCancel={handleCancelModal}
    >
      <Wrapper>
        <PartnerNoteWrapper>
          <InForWrapper>
            <BasicInfo
              data={entity}
              isLoading={isLoading}
              partnerCode={prevCode}
            >
              <UpdateCodeFormModal
                form={form}
                prevCode={prevCode}
                onUpdatePrevCode={handleUpdatePrevCode}
              />
            </BasicInfo>
          </InForWrapper>
          <PartnerNoteHeader>
            <Text color="dark" weight="bold">
              Danh sách ({notes.length})
            </Text>
            <Row styles={{ gap: 12 }} width="fit-content">
              <Icon
                styles={{ cursor: 'pointer' }}
                svgProps={{
                  icon: ICONS.COPY,
                  width: '18px',
                  height: '18px',
                  color: theme.colors.text.primary,
                }}
                tooltip="Sao chép"
                onClick={handleCopyText}
              />
              <AddButton onClick={handleShowCreateNote}>
                <Icon
                  svgProps={{
                    icon: ICONS.PLUS,
                    width: '12px',
                    height: '12px',
                    color: '#fff',
                  }}
                />
              </AddButton>
            </Row>
          </PartnerNoteHeader>
          <PartnerNoteContent>
            {isLoading ? (
              getDummyArray(5).map(item => <PartnerNoteSkeleton />)
            ) : (
              <>
                {!isCreateMode && notes.length == 0 && (
                  <Empty img={dataEmpty.COMMENT} minHeight={320} />
                )}
                {isCreateMode && activeFormID == 'CREATE_NOTE' && (
                  <PartnerNoteEditor
                    title=""
                    onCancel={handleCancelCreateNote}
                    onSubmit={handleCreateNote}
                  />
                )}
                {notes.map((item: any) => (
                  <PartnerNoteItem
                    activeFormID={activeFormID}
                    data={item}
                    onActiveFormID={handleActiveFormID}
                    onDeleteNote={handleDeleteNote}
                    onUpdateNote={handleUpdateNote}
                  />
                ))}
              </>
            )}
          </PartnerNoteContent>
        </PartnerNoteWrapper>
      </Wrapper>
    </Modal>
  );
};

export default PartnerNoteModal;

const InForWrapper = styled.div`
  padding: 0px ${p => p.theme.app.padding.horizontal};
`;
